import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { a as toValue } from './index5.js';
import { i as isLavOption } from './types.js';
import { i as isDefined } from './index4.js';
import { H as HiddenHTMLSelect } from './hidden-html-select.js';
import { d as defineCustomElement$9 } from './box.js';
import { d as defineCustomElement$8 } from './check-input.js';
import { d as defineCustomElement$7 } from './check-input-group.js';
import { d as defineCustomElement$6 } from './form-field.js';
import { d as defineCustomElement$5 } from './icon.js';
import { d as defineCustomElement$4 } from './status.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const checkInputPanelCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.inner{border:1px solid var(--jw-theme-form-border-regular-default-border-color);border-radius:.375rem;display:flex;flex-direction:column;height:100%;width:100%}.header{background-color:var(--jw-theme-surface-elevated-default-bg-color);border-bottom:1px solid var(--jw-theme-surface-divider-color);border-top-left-radius:.375rem;border-top-right-radius:.375rem;padding:.75rem 1rem}.body{background-color:var(--jw-theme-surface-level1-default-bg-color);border-bottom-left-radius:.375rem;border-bottom-right-radius:.375rem;max-height:25.25rem;overflow-y:auto;padding:1rem}";
const WuiCheckInputPanelStyle0 = checkInputPanelCss;

const WuiCheckInputPanel$1 = /*@__PURE__*/ proxyCustomElement(class WuiCheckInputPanel extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.handleCheckAllInput = (e) => {
            // Stop the inner event from bubbling up
            e.stopPropagation();
            if (this.areAllOptionsChecked()) {
                this.setValue([]);
            }
            else {
                this.setValue(this.options
                    ?.map((o) => {
                    if (isLavOption(o)) {
                        return toValue(o);
                    }
                    return undefined;
                })
                    .filter(isDefined));
            }
        };
        this.areSomeOptionsChecked = () => {
            if (typeof this.value === 'undefined' || this.value.length === 0) {
                return false;
            }
            if (typeof this.options === 'undefined' || this.options.length === 0) {
                return false;
            }
            return this.value.length !== this.options.filter((v) => isLavOption(v)).length;
        };
        this.areAllOptionsChecked = () => {
            if (typeof this.value === 'undefined' || this.value.length === 0) {
                return false;
            }
            if (typeof this.options === 'undefined' || this.options.length === 0) {
                return false;
            }
            return this.value.length === this.options.filter((v) => isLavOption(v)).length;
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.options = undefined;
        this.name = undefined;
        this.disabled = false;
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    setValue(value) {
        this.dirty = true;
        this.value = value;
        requestAnimationFrame(() => {
            this.checkValidity();
        });
        this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
    }
    renderInner() {
        return (h("div", { class: "inner" }, h(HiddenHTMLSelect, { name: this.name, required: !!this.required, multiple: true, ref: this.setInputRef, disabled: this.disabled, options: this.options, value: this.value }), h("div", { class: "header" }, h("wui-check-input", { checked: this.areAllOptionsChecked(), disabled: this.disabled || (this.options ?? []).length === 0, onInput: (e) => this.handleCheckAllInput(e), label: this.areAllOptionsChecked() ? 'Deselect all' : 'Select all', indeterminate: this.areSomeOptionsChecked() })), h("div", { class: "body" }, h("wui-check-input-group", { options: this.options, value: this.value, onInput: (e) => {
                e.stopPropagation();
                const target = e.currentTarget;
                if (!target) {
                    return;
                }
                this.setValue(target.value);
            }, disabled: this.disabled }))));
    }
    render() {
        return (h(Host, { key: '6d4de6534c35b17fc4986d79e928e6f1bb838a76', ...getHostAttributes(this) }, h("wui-form-field", { key: '5f572cbf1584d1c8d42c8b6471b0af4d4050efc1', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get el() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiCheckInputPanelStyle0; }
}, [1, "wui-check-input-panel", {
        "value": [1040],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "options": [16],
        "name": [1],
        "disabled": [4],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-check-input-panel", "wui-box", "wui-check-input", "wui-check-input-group", "wui-form-field", "wui-icon", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-check-input-panel":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiCheckInputPanel$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-check-input-group":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiCheckInputPanel = WuiCheckInputPanel$1;
const defineCustomElement = defineCustomElement$1;

export { WuiCheckInputPanel, defineCustomElement };

//# sourceMappingURL=wui-check-input-panel.js.map